import React from 'react';
import { Link } from 'react-router-dom';
import {
  Shield,
  Smile,
  Camera,
  MapPin,
  AlertCircle,
  Users,
  Settings,
  Zap,
  Mic2,
  Map,
  Cloud,
  Share2,
} from 'lucide-react';
import heroimg from "../images/heroimg.png";
import bgimg from "../images/patternimg.png";

export default function Home() {
  const painPoints = [
    {
      icon: <AlertCircle className="w-12 h-12 text-indigo-600" />,
      title: 'Cognitive Overload',
      description: 'Juggling maps, weather, alerts, and music is exhausting.',
    },
    {
      icon: <Smile className="w-12 h-12 text-indigo-600" />,
      title: 'Anxiety',
      description: 'Uncertainty about the safest route and what\'s around the next corner.',
    },
    {
      icon: <Camera className="w-12 h-12 text-indigo-600" />,
      title: 'Lost Memories',
      description: 'Fumbling for a phone means missing out on capturing perfect moments.',
    },
    {
      icon: <MapPin className="w-12 h-12 text-indigo-600" />,
      title: 'Fragmented Tools',
      description: 'Five apps for navigation, music, photos, and emergencies, none of which talk to each other.',
    },
  ];

  const solutions = [
    {
      icon: <Shield className="w-16 h-16 text-emerald-500" />,
      title: 'Proactive Guardian',
      description: 'Sarayu\'s AI predicts and avoids risks, ensuring a safer journey.',
    },
    {
      icon: <Smile className="w-16 h-16 text-emerald-500" />,
      title: 'Personalized Concierge',
      description: 'It personalizes your journey, offering tailored recommendations and assistance.',
    },
    {
      icon: <Camera className="w-16 h-16 text-emerald-500" />,
      title: 'AI Photographer',
      description: 'It captures and organizes your precious memories, so you don\'t miss a moment',
    },
  ];

  const coreFeatures = [
    { icon: <MapPin className="w-8 h-8" />, name: 'Personalized Trip Planning' },
    { icon: <MapPin className="w-8 h-8" />, name: 'Hotels & Stays Identification' },
    { icon: <AlertCircle className="w-8 h-8" />, name: 'Real-Time Alerts' },
    { icon: <Users className="w-8 h-8" />, name: 'Group Trips' },
    { icon: <Settings className="w-8 h-8" />, name: 'Custom Preferences' },
    { icon: <Zap className="w-8 h-8" />, name: 'AI Assisted & Agentic AI' },
  ];

  const highlights = [
    { icon: <Mic2 className="w-8 h-8" />, name: 'Voice Conversation Enabled' },
    { icon: <Map className="w-8 h-8" />, name: 'Trip Summary & Highlights' },
    { icon: <Users className="w-8 h-8" />, name: 'Create, Share Groups & Invite Users' },
    { icon: <Map className="w-8 h-8" />, name: 'Live Map Views' },
    { icon: <Cloud className="w-8 h-8" />, name: 'Traffic, Weather & Incident Alerts' },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 heroimg">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-background to-emerald-50" style={{zIndex: -20}}/>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="animate-slideUp">
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                One Intelligent Companion for Every Journey
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Sarayu is your AI co-pilot. It thinks ahead, so you can be present.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="btn-primary text-center">
                  Join the Journey
                </Link>
                <Link to="/features" className="btn-secondary text-center">
                  See How It Works
                </Link>
              </div>
            </div>

            {/* Right Visual */}
            <div className="relative h-96 hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-400 to-emerald-400 rounded-3xl opacity-20 blur-3xl" />
              <div className="glass  rounded-3xl  flex items-center justify-center">
                <img src={heroimg} alt="Sarayu AI Companion" className="w-full h-full object-contain rounded-3xl opacity-80" />
                
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-20 bg-white bgsecimg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              The Problem: Travel's Emotional Rollercoaster
            </h2>
            <p className="text-xl text-gray-600">
              Travel should be about discovery, not stress.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {painPoints.map((point, index) => (
              <div
                key={index}
                className="glass rounded-2xl p-8 card-hover animate-fadeIn"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="mb-4">{point.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{point.title}</h3>
                <p className="text-gray-600 text-justify">{point.description}</p>
              </div>
            ))}
          </div>

          {/* Quote */}
          <div className="bg-gradient-to-r from-indigo-100 to-emerald-100 rounded-2xl p-8 md:p-12 text-center">
            <p className="text-2xl md:text-3xl font-bold text-gray-900 italic">
              "The journey between stress and joy is a single wrong turn."
            </p>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              The Solution – One Intelligent Companion
            </h2>
            <p className="text-xl text-gray-600">
              Sarayu handles the complexity, so you can focus on the experience.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <div
                key={index}
                className="glass rounded-2xl p-8 text-center card-hover animate-slideUp"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className="flex justify-center mb-6">{solution.icon}</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{solution.title}</h3>
                <p className="text-gray-600 text-lg text-justify">{solution.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section className="py-20 bg-white bgsecimg1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Core Features
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need for the perfect journey.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {coreFeatures.map((feature, index) => (
              <div
                key={index}
                className="glass rounded-xl p-6 flex items-center space-x-4 card-hover animate-fadeIn"
                style={{ animationDelay: `${index * 0.08}s` }}
              >
                <div className="text-indigo-600 flex-shrink-0">{feature.icon}</div>
                <h3 className="font-semibold text-gray-900">{feature.name}</h3>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link to="/features" className="btn-primary">
              Explore All Features
            </Link>
          </div>
        </div>
      </section>

      {/* Highlights Preview Section */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Highlights
            </h2>
            <p className="text-xl text-gray-600">
              Turn every trip into a story worth sharing.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-12">
            {highlights.map((highlight, index) => (
              <div
                key={index}
                className="glass rounded-xl p-6 text-center card-hover animate-slideUp"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex justify-center mb-4 text-emerald-500">{highlight.icon}</div>
                <h3 className="font-semibold text-gray-900 text-sm">{highlight.name}</h3>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link to="/highlights" className="btn-primary">
              View Highlights
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {/* <section className="py-20 bg-gradient-to-r from-indigo-600 to-emerald-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
            Ready to Transform Your Travels?
          </h2>
          <p className="text-xl text-black/90 mb-8">
            Join thousands of travelers who are discovering a smarter way to explore the world.
          </p>
          <Link
            to="/contact"
            className="inline-block px-8 py-4 bg-white text-indigo-600 font-bold rounded-lg hover:shadow-lg transition-all duration-300 hover:scale-105"
          >
            Join the Journey Today
          </Link>
        </div>
      </section> */}
    </div>
  );
}
